import { z } from 'zod';

/** «Мой день» прораба (ПР-01): план на день или неделю по проектам, где у человека есть доступ к площадке. */
export const SiteMyDayQuery = z.strictObject({
  date: z.iso.date().optional(),
  /** 1 — один день, 7 — неделя вперёд от даты */
  days: z.coerce.number().int().min(1).max(14).default(1),
});
export type SiteMyDayQuery = z.infer<typeof SiteMyDayQuery>;

export interface SiteMyDayResource {
  kind: 'labor' | 'crew' | 'equipment';
  name: string;
  units: number;
}

export interface SiteMyDayLine {
  activityId: string;
  name: string;
  unit: string | null;
  totalQuantity: string | null;
  actualQuantity: string | null;
  /** Объём на день из сменного задания графика; null — по работе объём на день не задан */
  dayQuantity: string | null;
  resources: SiteMyDayResource[];
}

export interface SiteMyDayPackage {
  packageId: string;
  name: string;
  dateFrom: string;
  dateTo: string;
  contractorName: string | null;
  foremanName: string | null;
  lines: SiteMyDayLine[];
}

export interface SiteMyDayPermit {
  id: string;
  number: string;
  kind: string;
  title: string;
  place: string | null;
  validFrom: string;
  validUntil: string;
  status: string;
  activityName: string | null;
}

/** Поставка по графику договора, которую нужно принять на площадке. Суммы не передаются. */
export interface SiteMyDayDelivery {
  id: string;
  contractId: string;
  contractNumber: string;
  supplierId: string | null;
  supplierName: string | null;
  plannedDate: string;
  description: string | null;
  quantity: number | null;
  /** Уже принята на площадке (ПР-08) */
  acceptance: {
    id: string;
    receivedQuantity: number;
    passportPresent: boolean;
    remarkId: string | null;
    acceptedAt: string;
  } | null;
}

export interface SiteMyDayRemark {
  id: string;
  number: string;
  title: string;
  source: string;
  status: string;
  dueDate: string;
  overdue: boolean;
  stopWork: boolean;
  mine: boolean;
}

export interface SiteMyDayReport {
  id: string;
  reportDay: string;
  state: string;
  packageId: string;
}

export interface SiteMyDayProject {
  projectId: string;
  code: string;
  name: string;
  canReport: boolean;
  canIssueRemark: boolean;
  packages: SiteMyDayPackage[];
  /** null — у человека нет права «Видеть качество и ОТ», раздел скрыт */
  permits: SiteMyDayPermit[] | null;
  /** null — нет права на склад, договоры или отчёт смены (приёмка на площадке) */
  deliveries: SiteMyDayDelivery[] | null;
  /** null — нет права на реестр замечаний */
  remarks: SiteMyDayRemark[] | null;
  reports: SiteMyDayReport[];
}

/** Срок сдачи отчёта (ПР-05) в ответе «Мой день» — чтобы прораб видел час без отдельного запроса. */
export interface SiteMyDayDeadline {
  hour: number;
  timezone: string;
  /** Дата «сегодня» в поясе компании */
  today: string;
  pastDeadline: boolean;
}

export interface SiteMyDay {
  from: string;
  to: string;
  days: number;
  deadline: SiteMyDayDeadline;
  projects: SiteMyDayProject[];
}
